/**
 * C-GTCC-VIEW-OVW1 — die reine URL-Mechanik von I-GTCC-47, ohne Registry.
 *
 * `deep-link.ts` importiert die Registry, um einen View-Schluessel zu pruefen.
 * Die Quick Actions der Operations Overview brauchen dieselbe Schreiblogik,
 * und damit entstand der Zyklus
 *
 *   registry.ts -> views/ovw/OvwView.tsx -> deep-link.ts -> registry.ts
 *
 * Betritt ein Modulgraph diesen Zyklus ueber `OvwView.tsx` (genau das tut
 * `registry.test.ts`), dann baut `registry.ts` sein Array, bevor der Export
 * von `OvwView` gebunden ist: `GTCC_VIEWS` traegt fuer `ovw` dann `undefined`.
 * Gemessen, nicht vermutet — eine Sonde zeigte `component === OvwView` bei
 * Einstieg ueber `registry.js` und `undefined` bei Einstieg ueber die View.
 *
 * Dieses Blatt-Modul importiert nichts und beendet den Zyklus. Die Pruefung
 * eines Schluessels gegen die Registry bleibt in `deep-link.ts` und wird hier
 * als `resolvePathKey` hereingereicht; das Verhalten der bestehenden Exporte
 * bleibt dadurch unveraendert.
 */

/** Legacy-Parameter fuer bestehende Links. */
export const GTCC_VIEW_PARAM = "gtcc";

/** Unbekannte kanonische Pfade landen bewusst auf der Uebersicht. */
export const GTCC_UNKNOWN_VIEW_FALLBACK_KEY = "ovw";

/**
 * Views mit eigenen Tabs (z.B. `HwView`, `TenView`, `DplaneView`) schreiben
 * ihren aufgeloesten Tab selbst hierher (D-GTCC-ACCEPT-SHELL1), damit die
 * Breadcrumb ihn ab dem ersten Render zeigt. Der Parameter ist View-gebunden:
 * bleibt er beim Wechsel auf eine andere View stehen, haengt der Tab einer
 * fremden View (z.B. `hw`s `approvals`) an einer tablosen View (z.B. `gw`).
 */
const GTCC_TAB_PARAM = "tab";

export interface GtccBrowserLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

function basePath(baseUrl: string | undefined): string {
  if (!baseUrl || baseUrl === "/") return "";
  return `/${baseUrl.replace(/^\/+|\/+$/g, "")}`;
}

function routePrefix(baseUrl: string | undefined): string {
  return `${basePath(baseUrl)}/gtcc/`;
}

function decodeViewKey(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** `null` means that the location is not using the path contract. */
export function gtccRequestedPathKey(pathname: string, baseUrl: string | undefined): string | null {
  const prefix = routePrefix(baseUrl);
  if (!pathname.startsWith(prefix)) return null;

  const tail = pathname.slice(prefix.length).replace(/\/+$/, "");
  if (!tail || tail.includes("/")) return "";
  return decodeViewKey(tail);
}

/** Legacy View -> Query-URL. */
export function gtccUrlForViewKey(href: string, key: string): string {
  const url = new URL(href);
  url.searchParams.set(GTCC_VIEW_PARAM, key);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Bereits aufgeloester View -> build-base-bewusster Pfad, Rest-Query bleibt. */
export function gtccPathForResolvedViewKey(
  location: GtccBrowserLocation,
  resolvedKey: string,
  baseUrl: string | undefined,
): string {
  const params = new URLSearchParams(location.search);
  params.delete(GTCC_VIEW_PARAM);
  const search = params.toString();
  return `${routePrefix(baseUrl)}${encodeURIComponent(resolvedKey)}${search ? `?${search}` : ""}${location.hash}`;
}

/** Aktueller View-Schluessel aus Pfad oder Legacy-Query, roh (ungeprueft). */
function currentViewKeyFromLocation(
  location: GtccBrowserLocation,
  baseUrl: string | undefined,
): string | null {
  const pathKey = gtccRequestedPathKey(location.pathname, baseUrl);
  if (pathKey !== null) return pathKey;
  return new URLSearchParams(location.search).get(GTCC_VIEW_PARAM);
}

/** Entfernt einen stehengebliebenen View-eigenen Tab aus einer Adresse. */
function withoutTabParam(next: string): string {
  const url = new URL(next, "http://gtcc.invalid");
  if (!url.searchParams.has(GTCC_TAB_PARAM)) return next;
  url.searchParams.delete(GTCC_TAB_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Schreibt die View in die Adresse. Eine Pfad-Adresse bleibt im Pfadmodus;
 * eine Legacy-Query-Adresse behaelt ihr kompatibles Schema.
 *
 * `resolvePathKey` prueft den Schluessel im Pfadmodus. Der Standard laesst ihn
 * unveraendert — wer einen Schluessel aus der Registry uebergibt, braucht keine
 * zweite Pruefung; `deep-link.ts` reicht die Registry-Pruefung herein.
 *
 * Wechselt dabei der View-Schluessel tatsaechlich, faellt ein `?tab=` der
 * vorigen View weg (siehe `GTCC_TAB_PARAM`); die neue View schreibt ihren
 * eigenen Tab, falls sie einen hat, aus ihrem eigenen Mount-Effekt nach.
 */
export function writeGtccViewKeyToLocation(
  key: string,
  replace = false,
  baseUrl?: string,
  resolvePathKey: (key: string) => string = (k) => k,
): void {
  if (typeof window === "undefined") return;
  const pathMode = gtccRequestedPathKey(window.location.pathname, baseUrl) !== null;
  const previousKey = currentViewKeyFromLocation(window.location, baseUrl);
  const resolvedKey = pathMode ? resolvePathKey(key) : key;
  let next = pathMode
    ? gtccPathForResolvedViewKey(window.location, resolvedKey, baseUrl)
    : gtccUrlForViewKey(window.location.href, key);
  if (previousKey !== null && previousKey !== resolvedKey) {
    next = withoutTabParam(next);
  }
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (replace) {
    window.history.replaceState(window.history.state, "", next);
    return;
  }
  if (next === current) return;
  window.history.pushState(window.history.state, "", next);
}
